/** @format */

import { CsvImport } from "../../components/CsvImport";
import { useState } from "react";
import { CsvValidation } from "../../components/CsvValidation";
import { PdfImport, type ImportPdfKeys } from "../../components/PdfImport";
import { PdfImportStart } from "../../components/PdfImportStart";

interface TabButtonProps {
	activeTab: TabKey;
	onTabChange: (tab: TabKey) => void;
	tabLabels: Record<TabKey, string>;
}
interface TabContentProps {
	activeTab: TabKey;
	onPdfUploaded: (keys: ImportPdfKeys | null) => void;
}

const getTabStyle = (isActive: boolean) =>
	isActive
		? "h-[36px] py-[6px] px-[16px] gap-1 text-[14px] whitespace-nowrap text-(--admin-color-primary-strong) cursor-pointer border-b-2 font-roboto font-semibold"
		: "h-[36px] py-[6px] px-4 gap-1 text-[14px] whitespace-nowrap cursor-pointer text-slate-500 font-semibold";

const TAB_KEYS = ["csvImport", "pdfImport"] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TabButtons = ({ activeTab, onTabChange, tabLabels }: TabButtonProps) => {
	return (
		<div className="flex" role="tablist">
			{TAB_KEYS.map((key) => (
				<button
					type="button"
					key={key}
					role="tab"
					aria-selected={activeTab === key}
					onClick={() => onTabChange(key)}
					className={getTabStyle(activeTab === key)}
				>
					{tabLabels[key]}
				</button>
			))}
		</div>
	);
};
const TabContent = ({ activeTab, onPdfUploaded }: TabContentProps) => {
	const [file, setFile] = useState<File | null>(null);

	if (activeTab === "csvImport") {
		return (
			<div className="mt-4 px-6">
				<CsvImport onFileReady={setFile} />
				{file && <CsvValidation csvFile={file} />}
			</div>
		);
	}
	if (activeTab === "pdfImport") {
		return (
			<div className="mt-4 px-6">
				<PdfImport onKeysChange={onPdfUploaded} />
			</div>
		);
	}
};

export const AdminImportQuestionPage = () => {
	const [activeTab, setActiveTab] = useState<TabKey>("csvImport");
	const [pdfKeys, setPdfKeys] = useState<ImportPdfKeys | null>(null);

	const tabLabels: Record<TabKey, string> = {
		csvImport: "Import CSV",
		pdfImport: "Import PDF",
	};

	return (
		<div>
			<div className="mb-4">
				<h2 className="text-[1.75rem] font-bold text-(--admin-color-primary)">Import Questions</h2>
			</div>

			<div>
				<TabButtons
					activeTab={activeTab}
					onTabChange={(tab) => {
						setActiveTab(tab);
						setPdfKeys(null);
					}}
					tabLabels={tabLabels}
				/>
			</div>
			<div>
				<TabContent activeTab={activeTab} onPdfUploaded={setPdfKeys} />
				{activeTab === "pdfImport" && pdfKeys && (
					<div className="px-6">
						<p className="mt-4 text-sm text-green-700">All three PDFs uploaded.</p>
						<PdfImportStart keys={pdfKeys} />
					</div>
				)}
			</div>
		</div>
	);
};
